/**
 * /competitive-analysis-template 的中文版本 —— 命中「竞品分析模板 /
 * 竞品分析模板 表格 / 竞品分析案例」搜索词簇。
 *
 * 差异化角度:写在文档或幻灯片里的竞品分析模板就是个填空格式 —— 你刚填完,
 * 它描述的那个市场下一周就变了。对手改价、重新定位、上线你标成「空白」的功能,
 * 这份分析转眼成了没人敢信的化石。本页先把真模板(八个字段,逐条写清)给搜索者,
 * 让来的人拿到能用的东西,再说明 Draftlize 把每一条竞品观察变成结构化卡片、
 * 连到你的定位决策 —— 对手一动,依赖旧判断的卡片自动标 stale。
 *
 * 中文 + 内联(无 i18n),对齐 DecisionLogTemplateZh.tsx 与各对比页。
 * 英文版在 CompetitiveAnalysisTemplate.tsx。
 */
import { MarketingShell } from './MarketingShell';
import { TemplateCta } from './TemplateCta';

export function CompetitiveAnalysisTemplateZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/competitive-analysis-template"
      documentTitle="竞品分析模板(不会过时的那种)· Draftlize"
      documentDescription="一份可以直接抄走的竞品分析模板 —— 竞品、对比维度、它的优势、它的劣势、它的定位与定价、我们的差异化、威胁与机会、最后核实时间。但有个前提:写在文档或幻灯片里的竞品分析模板,你刚填完那一周就开始过时,因为对手会改价、会重新定位。在 Draftlize 里,每一条竞品观察都是一张结构化卡片,连到你的定位决策;对手一动,依赖它的卡片自动标 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">模板 · 竞品分析</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            一份竞品分析模板,<br/>
            <em>不会在你做完那一周就过时。</em>
          </h1>
          <p className="lede">
            你来找的是一份<em>竞品分析模板</em> —— 那些列、那些表头,好在下次战略评审前直接塞进文档或幻灯片。它就在下面,拿走。但你得清楚自己拿的是什么:竞品分析是一张「移动中的市场」的快照,而市场会在你做完的那一周继续移动。某个对手改了价、换了说法、上线了你标成「空白」的功能 —— 你那张排版精美的表就这么悄悄错了,而且你根本看不出是哪几行错了。Draftlize 给你同样的八个字段,但把每一个都变成 AI agent 既能读也能写的卡片,连到它所支撑的那个定位决策;对手一动,所有建立在旧判断之上的东西都会自己标成 stale。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">这份模板</span>
            <h2 className="serif">八个字段,<em>每个对手填一份。</em></h2>
          </div>
          <p>这就是模板的全部 —— 每盯一个对手,就填一组。文档、wiki、表格、幻灯片里都能用,拿去直接套就行,它本身就是一份不错的<em>竞品分析模板</em>。下一节讲的是:当每个字段不再是文字、而是一张「知道自己什么时候过时了」的卡片,会发生什么变化。</p>
        </div>

        <div
          style={{
            border: '1px solid var(--ink-0)',
            background: 'var(--paper-0)',
            fontFamily: "'Inter', sans-serif",
          }}
        >
          {[
            {
              k: '竞品',
              hint: '是谁',
              body: '你要掂量的那个对手,直接点名。可以是正面对手、邻近工具,也可以是「凑合够用」的现状(一张表格、什么都不做)—— 最后这一类,恰恰是大多数分析忘了列进去的最强对手。',
            },
            {
              k: '对比维度',
              hint: '比哪些轴',
              body: '你拿来评判的标准 —— 是决定一单成交的那几条,不是功能清单。挑真正能左右买家的四五个;在二十个维度上铺开比,只会把真正要紧的那两个埋掉。',
            },
            {
              k: '它的优势',
              hint: '它赢在哪',
              body: '它确实比你强的地方,如实写、别躲。诚实的版本才是唯一有用的版本 —— 一份为了让你自我感觉良好而写的竞品分析,是一份你没法据以行动的竞品分析。',
            },
            {
              k: '它的劣势',
              hint: '它软在哪',
              body: '你能下手按住的口子:缺的能力、笨拙的流程、服务不到位的人群。每一条都标清是结构性的(它很难补)还是临时性的(下季度就上线)—— 这一字之差能改变全盘判断。',
            },
            {
              k: '它的定位与定价',
              hint: '说法 + 数字',
              body: '它怎么给自己定调、收多少钱 —— 主打的那句话、计费模式(按席位 / 按量 / 一口价)、官网标价。这是烂得最快的一个字段,因为定价页改了从来不会通知你。',
            },
            {
              k: '我们的差异化',
              hint: '为什么选我们',
              body: '针对这个具体对手,你做得到、它不容易抄走的那一件事。不是功能列表 —— 是那个楔子。如果你没法逐个对手把它说清,这份分析就刚好替你暴露了一个真实的战略问题。',
            },
            {
              k: '威胁与机会',
              hint: '所以呢',
              body: '这次对比指向的那个动作:要防住的威胁,要抢下的开口。没有「所以」的分析只是谈资。这一栏,才是读者真正翻开这份文档要找的东西。',
            },
            {
              k: '最后核实时间',
              hint: '什么时候',
              body: '你上一次拿以上内容对照现实核实的日期。最重要、却几乎被所有模板省掉的一个字段 —— 没有它,你根本分不清眼前是一份当下的判断还是一具化石。',
            },
          ].map((f, i) => (
            <div
              key={f.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '220px 1fr',
                gap: 24,
                padding: '20px 22px',
                borderTop: i === 0 ? 0 : '1px dashed var(--rule)',
                alignItems: 'baseline',
              }}
            >
              <div>
                <span
                  className="serif"
                  style={{ fontSize: 19, fontWeight: 500, color: 'var(--ink-0)', display: 'block', lineHeight: 1.2 }}
                >
                  {f.k}
                </span>
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9.5,
                    letterSpacing: '.1em',
                    textTransform: 'uppercase',
                    color: 'var(--amber)',
                    fontWeight: 700,
                  }}
                >
                  {f.hint}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-1)' }}>{f.body}</p>
            </div>
          ))}
        </div>

        <p
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: '.06em',
            color: 'var(--ink-3)',
            textAlign: 'center',
            marginTop: 18,
          }}
        >
          模板就到这。下面讲:为什么市场会在你做完那一周就让它过时 —— 以及我们怎么解决。
        </p>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">竞品分析为什么<em>会过时。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">它是给移动中的对象拍的照片</h3>
            <p>竞品分析定格的是你动笔那天的对手。可对手不会站着不动 —— 它会改价、换牌、上新。你(多半没记)的那个「最后核实时间」,是当下判断和化石之间唯一的隔断;而一份文档既没法让某一行自己变旧,也不会提醒你它已经过时了。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">定价和定位,最先开始说谎</h3>
            <p>「它的定位与定价」这个字段烂得最快。对手悄悄改了定价页,你的幻灯片还显示上季度那个数 —— 于是你建立在它之上的差异化、你正想靠它拿下的那一单,全都跑在没人标记过的过期情报上。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">做完那一刻就是最高水位</h3>
            <p>这份分析最准的时候,是战略评审刚结束那一分钟,此后再也没那么准过。到下个季度,实打实有一部分是错的,而你看不出是哪一部分 —— 于是整份文档被悄悄打上不信任,这比没有还糟。它最后不是被更新,而是被推倒重做。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">同一份模板,<em>变活。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">每条观察是一张卡片,不是一个格子</h3>
            <p>在 Draftlize 里,模板不是一张静态表格 —— 每一条竞品观察都是一张带着那几个字段的结构化卡片:竞品、对比维度、优势、劣势、定位与定价、差异化、威胁与机会、最后核实时间。有结构、按 ID 可寻址,能从一个定位决策或一个对话里直接引用,而不是埋在某份幻灯片里。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">对手一动,依赖它的卡片标 stale</h3>
            <p>把每张竞品卡片连到它所支撑的定位决策。当某个对手改价或重新定位、你更新它那张卡片时,所有建立在旧判断之上的决策和 spec 都会自动标成 stale —— 就像构建系统让一个被改动文件下游的一切全部失效。你的差异化,再也不会悄悄地继续靠一个早已不成立的数字撑着。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">一个 AI agent 读它、也写它</h3>
            <p>Claude Code 或 Cursor 通过 MCP,在动手起草你的定位、你的发布文案、下一份 spec 之前,先读完每一张相关的竞品卡片 —— 再把新的观察按同样的结构写回去。这份分析不再是写给一个再也不回来的评审者的文档,而成了每一回合都会被读到的上下文。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          竞品分析模板能告诉你该把对手的哪些信息记下来。它没法告诉你:对手已经动了,把这些记录变错了。
        </blockquote>
        <cite>八个字段留着。让底座替你保持它们当下有效。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '什么是竞品分析？', a: '竞品分析是把你的产品和对手在要紧维度上做结构化对比——优势、劣势、定位、定价、差异化——让你看清自己在哪里真的赢、哪里被对手威胁。它的作用是给定位和策略提供依据，而不是收集一堆 logo。' },
            { q: '竞品分析模板应该包含什么？', a: '对每个对手：你对比的维度、它的优势与劣势、它的定位与定价、你对它的差异化、它带来的威胁与机会，以及你最后一次核实的日期。最后这个字段——最后核实——正是告诉你这份判断是当下的还是已经旧了一年。' },
            { q: '竞品分析应该多久做一次？', a: '精神上要持续，形式上每季度、或在重大发布或定位决策前做一次。陷阱是把它当一次性交付物：对手一改价或一换定位，分析就悄悄错了，所以持久的做法是更新你动过的那个对手，而不是把整份文档重建。' },
            { q: 'Draftlize 怎么让竞品分析保持当下有效？', a: '每个对手都是一张连着它所影响定位决策的卡片。对手改价后你更新它的卡片，凡是建立在旧判断上的决策或 spec 都自动标成 stale——于是你的差异化，不会继续悄悄押在一个早已不成立的数字上。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">开始你的竞品分析,<em>注册送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>按量付费,用多少付多少</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把上面那份模板拿走,或者开一个项目,让 agent 从你已有的调研里把它填好 —— 再由它替你,在市场移动时把每一条竞品判断、以及依赖它的定位,始终保持当下有效。
          </p>
          <a className="btn-primary" href="/">注册送 $5,免费开始</a>
        </div>
      </section>
      <TemplateCta template="competitive-analysis" locale="zh" />
    </MarketingShell>
  );
}
